import {
  ChevronDownIcon,
  ChevronRightIcon,
  FileUpIcon,
  ShieldIcon,
  Trash2Icon,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'

import { errorMessage } from '@/api/client'
import { downloadSecureFile } from '@/api/endpoints'
import {
  UPLOADS_PAGE_SIZE,
  useMe,
  useRemoveUpload,
  useSecureFiles,
  useUploadDownloads,
  useUploads,
} from '@/api/queries'
import type { SecureFileDto, UploadDto, UploadStatus, ZoneSummary } from '@/api/types'
import { PageBody, PageHeader } from '@/components/AppShell'
import { RequireZone } from '@/components/RequireZone'
import { EmptyState, ErrorState, TableSkeleton } from '@/components/states'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button, buttonVariants } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { formatAbsolute, formatBytes, pluralise } from '@/lib/format'
import { cn } from '@/lib/utils'
import { useReadOnly } from '@/staff/hooks'

/**
 * The record that files were passed between agents (D31, work item 8).
 *
 * There is no download control on this page and no route that could feed one:
 * `UploadDto` has no bytes, and the only way a file leaves the bucket is a
 * recipient agent's one-time URL. What the console shows is the exchange —
 * who offered what to whom, whether it was stored, who fetched it and when —
 * plus one administrator action: removing a stored file (decision 9a).
 */
function statusBadge(status: UploadStatus) {
  switch (status) {
    case 'PENDING':
      return <Badge variant="outline">awaiting bytes</Badge>
    case 'STORED':
      return <Badge variant="success">stored</Badge>
    case 'REJECTED':
      return <Badge variant="destructive">rejected</Badge>
    case 'FAILED':
      return <Badge variant="destructive">failed</Badge>
    case 'QUARANTINED':
      return <Badge variant="destructive">quarantined</Badge>
    case 'EXPIRED':
      return <Badge variant="secondary">expired</Badge>
    case 'DELETED':
      return <Badge variant="secondary">removed</Badge>
  }
}

/** The one line under the status that says why a file is not fetchable. */
function statusNote(upload: UploadDto): ReactNode {
  if (upload.status === 'DELETED') {
    return (
      <span className="text-xs text-muted-foreground">
        removed{upload.deleted_by ? ` by ${upload.deleted_by}` : ''}
        {upload.deleted_at ? ` on ${formatAbsolute(upload.deleted_at)}` : ''}
      </span>
    )
  }
  if (
    (upload.status === 'QUARANTINED' || upload.status === 'REJECTED' || upload.status === 'FAILED') &&
    upload.reject_reason
  ) {
    return <span className="text-xs text-destructive">{upload.reject_reason}</span>
  }
  return null
}

function DownloadLog({ zoneId, handle }: { zoneId: string; handle: string }) {
  const events = useUploadDownloads(zoneId, handle, true)
  if (events.isError) {
    return (
      <p className="text-xs text-destructive">
        Could not load the download log: {errorMessage(events.error)}
      </p>
    )
  }
  if (events.isPending) return <TableSkeleton rows={2} columns={4} />
  if (events.data.length === 0) {
    return <p className="text-xs text-muted-foreground">No download has been attempted yet.</p>
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Recipient</TableHead>
          <TableHead>Started</TableHead>
          <TableHead>Completed</TableHead>
          <TableHead>Bytes served</TableHead>
          <TableHead>Outcome</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {events.data.map((event, index) => (
          <TableRow key={`${event.recipient}-${event.started_at}-${String(index)}`}>
            <TableCell className="font-mono text-sm">{event.recipient}</TableCell>
            <TableCell className="text-xs text-muted-foreground tabular">
              {formatAbsolute(event.started_at)}
            </TableCell>
            <TableCell className="text-xs text-muted-foreground tabular">
              {event.completed_at ? formatAbsolute(event.completed_at) : '—'}
            </TableCell>
            <TableCell className="text-xs tabular">{formatBytes(event.bytes_served)}</TableCell>
            <TableCell>
              {event.outcome === 'COMPLETED' ? (
                <Badge variant="success">completed</Badge>
              ) : (
                <Badge variant="destructive">aborted</Badge>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

/** The administrator's one action. Bytes go now; the record stays (decision 9a). */
function RemoveFileControl({
  zoneId,
  upload,
  isAdmin,
}: {
  zoneId: string
  upload: UploadDto
  isAdmin: boolean
}) {
  const [confirming, setConfirming] = useState(false)
  const remove = useRemoveUpload()
  const readOnly = useReadOnly()

  if (readOnly) return null
  if (upload.status !== 'STORED' && upload.status !== 'QUARANTINED') return null

  if (!isAdmin) {
    // A disabled button fires no pointer events, so the tooltip hangs off a
    // wrapping span rather than the button itself.
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0} className="inline-flex">
            <Button variant="outline" size="sm" disabled aria-label={`Remove ${upload.filename}`}>
              <Trash2Icon className="size-3.5" />
              Remove file
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>Only an account administrator can remove files</TooltipContent>
      </Tooltip>
    )
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setConfirming(true)}
        disabled={remove.isPending}
        aria-label={`Remove ${upload.filename}`}
      >
        <Trash2Icon className="size-3.5" />
        Remove file
      </Button>

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this file?</AlertDialogTitle>
            <AlertDialogDescription>
              The bytes of <span className="font-mono">{upload.filename}</span> are deleted from
              storage immediately. Recipients who have not downloaded it yet will no longer be able
              to. The record of the exchange stays on this page.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <Alert>
            <AlertTitle>Removal is not recall</AlertTitle>
            <AlertDescription>
              Anyone who already downloaded the file keeps their copy. This stops future downloads
              only.
            </AlertDescription>
          </Alert>

          {remove.isError ? (
            <Alert variant="destructive">
              <AlertTitle>Could not remove the file</AlertTitle>
              <AlertDescription>{errorMessage(remove.error)}</AlertDescription>
            </Alert>
          ) : null}

          <AlertDialogFooter>
            <AlertDialogCancel disabled={remove.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={cn(buttonVariants({ variant: 'destructive' }))}
              disabled={remove.isPending}
              onClick={(event) => {
                event.preventDefault()
                remove.mutate(
                  { zoneId, handle: upload.handle },
                  { onSuccess: () => setConfirming(false) },
                )
              }}
            >
              {remove.isPending ? 'Removing…' : 'Yes, remove it'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

function UploadRow({
  zoneId,
  upload,
  isAdmin,
}: {
  zoneId: string
  upload: UploadDto
  isAdmin: boolean
}) {
  const [open, setOpen] = useState(false)
  const Chevron = open ? ChevronDownIcon : ChevronRightIcon

  return (
    <>
      <TableRow>
        <TableCell className="w-8 pr-0">
          <Button
            variant="ghost"
            size="sm"
            className="size-7 p-0"
            onClick={() => setOpen((value) => !value)}
            aria-expanded={open}
            aria-label={open ? 'Hide download log' : 'Show download log'}
          >
            <Chevron className="size-4" />
          </Button>
        </TableCell>
        <TableCell className="font-mono text-xs text-muted-foreground">{upload.handle}</TableCell>
        <TableCell className="max-w-[16rem]">
          {/* Caller-supplied text, rendered as text. Never markup. */}
          <span className="block truncate text-sm" title={upload.filename}>
            {upload.filename}
          </span>
          <span className="block truncate font-mono text-xs text-muted-foreground">
            {upload.detected_content_type ?? upload.declared_content_type}
          </span>
          {upload.is_secret ? (
            <span className="mt-1 flex flex-wrap items-center gap-1">
              <Badge variant="secondary">secure</Badge>
              <span className="text-xs text-muted-foreground">
                {upload.status === 'STORED' && upload.secret_expires_at
                  ? `destroyed unclaimed ${formatAbsolute(upload.secret_expires_at)}`
                  : 'destroyed on delivery'}
              </span>
            </span>
          ) : null}
        </TableCell>
        <TableCell className="text-sm tabular">{formatBytes(upload.size_bytes)}</TableCell>
        <TableCell className="font-mono text-sm">{upload.sender}</TableCell>
        <TableCell>
          <ul className="space-y-0.5">
            {upload.recipients.map((recipient) => (
              <li key={recipient.name} className="text-xs">
                <span className="font-mono">{recipient.name}</span>{' '}
                <span className="text-muted-foreground">
                  {recipient.redeemed_at
                    ? `· fetched ${formatAbsolute(recipient.redeemed_at)}`
                    : recipient.download_attempts > 0
                      ? `· ${pluralise(recipient.download_attempts, 'attempt')}`
                      : '· not fetched'}
                </span>
              </li>
            ))}
          </ul>
        </TableCell>
        <TableCell>
          <div className="flex flex-col gap-1">
            {statusBadge(upload.status)}
            {statusNote(upload)}
          </div>
        </TableCell>
        <TableCell className="text-xs text-muted-foreground tabular">
          {formatAbsolute(upload.created_at)}
          {upload.stored_at ? (
            <span className="block">stored {formatAbsolute(upload.stored_at)}</span>
          ) : null}
        </TableCell>
        <TableCell className="text-right">
          <RemoveFileControl zoneId={zoneId} upload={upload} isAdmin={isAdmin} />
        </TableCell>
      </TableRow>
      {open ? (
        <TableRow>
          <TableCell colSpan={9} className="bg-muted/30">
            <div className="space-y-2 py-1">
              <p className="text-xs font-medium tracking-[0.06em] text-muted-foreground uppercase">
                Download log
              </p>
              <DownloadLog zoneId={zoneId} handle={upload.handle} />
            </div>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  )
}

/**
 * <strong>The secure files addressed to me</strong> (SECURE_FILES decision 10).
 *
 * <p>A secure file holds a credential, so this card shows metadata and nothing else — the same rule
 * the Secrets page states, applied here. Downloading is the only way to see the contents, it happens
 * under this person's own session with no URL or token ever reaching the page, and it
 * <strong>destroys the file</strong>. The card says so before the button is pressed, because a
 * download that cannot be repeated is not something to discover afterwards.
 */
function SecureFileInbox({ zoneId }: { zoneId: string }) {
  const waiting = useSecureFiles(zoneId)
  const [collecting, setCollecting] = useState<string | undefined>(undefined)
  const [failure, setFailure] = useState<string | undefined>(undefined)
  const files = waiting.data ?? []

  async function collect(file: SecureFileDto) {
    setCollecting(file.handle)
    setFailure(undefined)
    try {
      const { blob, filename } = await downloadSecureFile(zoneId, file.handle)
      // Handed straight to the browser's own download. The bytes are never put in React state,
      // never rendered, and the object URL is revoked as soon as the click is dispatched.
      const href = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = href
      link.download = filename ?? file.filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(href)
      await waiting.refetch()
    } catch (error) {
      setFailure(error instanceof Error ? error.message : 'The file could not be collected.')
    } finally {
      setCollecting(undefined)
    }
  }

  if (waiting.isPending || files.length === 0) {
    // No empty state on purpose: an inbox with nothing in it is the normal case for almost every
    // reader, and a permanent "nothing here" card is noise on a page that is not about them.
    return null
  }

  return (
    <Card data-testid="secure-file-inbox">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldIcon className="size-4 text-primary" />
          Addressed to you
        </CardTitle>
        <CardDescription>
          Secure files waiting for you in this zone. They hold credentials, so nothing about their
          contents is shown here. <strong>Downloading one destroys it</strong> — there is no second
          copy and no second chance, and the sender is told it reached you.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {failure === undefined ? null : (
          <Alert variant="destructive">
            <AlertTitle>Could not collect the file</AlertTitle>
            <AlertDescription>{failure}</AlertDescription>
          </Alert>
        )}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>File</TableHead>
              <TableHead>Size</TableHead>
              <TableHead>From</TableHead>
              <TableHead>Destroyed unclaimed</TableHead>
              <TableHead className="text-right">Collect</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {files.map((file) => (
              <TableRow key={file.handle} data-testid="secure-file-row">
                <TableCell className="max-w-[16rem]">
                  {/* Caller-supplied text, rendered as text. Never markup. */}
                  <span className="block truncate text-sm" title={file.filename}>
                    {file.filename}
                  </span>
                  <span className="block truncate font-mono text-xs text-muted-foreground">
                    {file.handle}
                  </span>
                </TableCell>
                <TableCell className="text-sm tabular">{formatBytes(file.size_bytes)}</TableCell>
                <TableCell className="font-mono text-sm">{file.sender}</TableCell>
                <TableCell className="text-xs text-muted-foreground tabular">
                  {formatAbsolute(file.expires_at)}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    size="sm"
                    disabled={collecting !== undefined}
                    onClick={() => {
                      void collect(file)
                    }}
                  >
                    {collecting === file.handle ? 'Collecting…' : 'Download and destroy'}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}

function UploadsView({ zone }: { zone: ZoneSummary }) {
  const me = useMe()
  const isAdmin = me.data?.role === 'ADMIN'
  const uploads = useUploads(zone.id)
  const rows = uploads.data ?? []

  return (
    <>
      <PageHeader
        title="Uploads"
        description="Files agents have passed to each other in this zone. The files themselves are never shown here — the console records the exchange and never holds the file."
      />
      <PageBody>
        <Alert>
          <AlertTitle>How this works</AlertTitle>
          <AlertDescription>
            An agent calls <code className="font-mono">upload</code> (op create) naming its recipients,
            sends the bytes over HTTPS to the URL it gets back, and tells the recipients the handle
            in an ordinary message. Each recipient calls{' '}
            <code className="font-mono">upload</code> (op consume) and fetches the file once through a
            one-time URL. Files follow the account&apos;s data retention; an administrator can remove
            one sooner.
          </AlertDescription>
        </Alert>

        <SecureFileInbox zoneId={zone.id} />

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileUpIcon className="size-4 text-primary" />
              Passed files
            </CardTitle>
            <CardDescription>
              Handle, file, size, sender, recipients, and what became of it. Newest first. Expand a
              row for its download log. Files are exchanged between agents; the console records the
              exchange and never holds the file.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {uploads.isError ? (
              <ErrorState
                error={uploads.error}
                title="Could not load the uploads record"
                onRetry={() => {
                  void uploads.refetch()
                }}
              />
            ) : uploads.isPending ? (
              <TableSkeleton rows={4} columns={6} />
            ) : rows.length === 0 ? (
              <EmptyState
                icon={FileUpIcon}
                title="No files have been passed in this zone"
                description="When an agent uses the upload tool, the record appears here."
              />
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-8" />
                        <TableHead>Handle</TableHead>
                        <TableHead>File</TableHead>
                        <TableHead>Size</TableHead>
                        <TableHead>From</TableHead>
                        <TableHead>To</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Offered</TableHead>
                        <TableHead className="text-right">
                          <span className="sr-only">Actions</span>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map((upload) => (
                        <UploadRow
                          key={upload.handle}
                          zoneId={zone.id}
                          upload={upload}
                          isAdmin={isAdmin}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <p className="pt-2 text-xs text-muted-foreground">
                  {pluralise(rows.length, 'file')} recorded
                  {rows.length >= UPLOADS_PAGE_SIZE ? ' — showing the most recent 200' : ''}.
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </PageBody>
    </>
  )
}

export function UploadsPage() {
  return <RequireZone>{(zone) => <UploadsView zone={zone} />}</RequireZone>
}
